import { Component, inject } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { combineLatest, filter, map, startWith } from 'rxjs';
import { UserRole } from '../core/constants/keys.constant';
import { AuthService } from '../services/auth.service';
import { GuidedTourService } from '../shared/services/guided-tour.service';
import { GuideViewerComponent } from './guide-viewer.component';
import { GuidesService, hasGuides } from './guides.service';

/**
 * Renders the open guide and the completion toast. Place once in the logged-in layout.
 *
 * Also binds Guides to the logged-in profile: the profile subject emits after login and
 * after a refresh restores the session, so both cases set the user and try the first-login guide.
 * After every navigation (and once guides load) it opens the guide for the module being viewed.
 * A `null` profile (logout) clears the previous user's guides.
 */
@Component({
  selector: 'mindbase-guide-host',
  standalone: true,
  imports: [GuideViewerComponent, TranslateModule],
  template: `
    @if (guides.openGuide(); as guide) {
      <mindbase-guide-viewer
        [guide]="guide"
        [startIndex]="guides.startIndex()"
        (closed)="guides.close($event)"
        (completed)="guides.complete()"
        (stepViewed)="guides.stepViewed($event)" />
    }
    @if (guides.toast(); as toast) {
      <div class="g-toast" role="status">{{ toast.key | translate: toast.params }}</div>
    }
  `,
  styles: `
    @use 'guides-theme' as theme;

    :host {
      @include theme.tokens;
    }

    .g-toast {
      position: fixed;
      left: 50%;
      bottom: calc(84px + env(safe-area-inset-bottom, 0px));
      transform: translateX(-50%);
      z-index: 1002;
      padding: 10px 18px;
      border-radius: 10px;
      background: var(--g-heading);
      color: var(--g-surface);
      font-family: var(--g-font-body);
      font-size: 14px;
      font-weight: 600;
    }
  `
})
export class GuideHostComponent {
  protected readonly guides = inject(GuidesService);
  private readonly authService = inject(AuthService);
  private readonly guidedTourService = inject(GuidedTourService);
  private readonly router = inject(Router);

  constructor() {
    const url$ = this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => this.router.url),
      startWith(this.router.url)
    );

    combineLatest([
      this.authService.profileDetailsSubject,
      this.guidedTourService.tourState$,
      url$,
      toObservable(this.guides.state)
    ])
      .pipe(takeUntilDestroyed())
      .subscribe(([profile, tour, url]) => {
        if (!profile?._id || !hasGuides(profile.role)) {
          this.guides.clearUser();
          return;
        }
        this.guides.setUser(profile._id);
        // Students also get the pathway guided tour on first login; wait until it is
        // finished so the two never open on top of each other.
        if (profile.role === UserRole.STUDENT && (tour.active || !this.guidedTourService.isCompleted)) return;
        this.guides.openFirstLogin();
        this.guides.openForRoute(url);
      });
  }
}
